import { useEffect } from "react";
import { App, AppState } from "@capacitor/app";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { BundleInfo, CapacitorUpdater } from "@capgo/capacitor-updater";
import { useConfig } from "../providers/config";

const codeBaseVersion = process.env.REACT_APP_PARHATO_CODE_VERSION!;

const useAppLiveUpdates = () => {
  const config = useConfig();

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !config.liveUpdate?.enabled) {
      console.log("[live-update] not native platform or live update is not enabled");
      return;
    }

    if (!config.liveUpdate?.version || Number(codeBaseVersion) >= Number(config.liveUpdate.version)) {
      console.log("[live-update] no live update needed");
      return;
    }

    let listenerHandle: PluginListenerHandle | null = null;
    let pendingBundle: BundleInfo | null = null;

    const applyPendingUpdate = async () => {
      if (!pendingBundle) {
        console.error("[live-update] no pending bundle found");
        return;
      }

      try {
        const currentBundle = await CapacitorUpdater.current();

        if (pendingBundle.version === currentBundle.bundle.version) {
          console.log("[live-update] pending bundle is already applied");
          return;
        }

        await CapacitorUpdater.set(pendingBundle);
        console.log("[live-update] applied pending bundle");
      } catch (err) {
        console.error("[live-update] failed to apply bundle", err);
      }
    };

    const resolvePendingBundle = async (): Promise<BundleInfo | null> => {
      const { version, url } = config.liveUpdate!;

      const list = await CapacitorUpdater.list();
      const currentBundle = await CapacitorUpdater.current();
      const downloadedBundle = list.bundles.find((bundle) => bundle.version === version);

      if (downloadedBundle && downloadedBundle.version !== currentBundle.bundle.version) {
        return downloadedBundle;
      }

      if (!url) {
        console.error("[live-update] no url found");
        return null;
      }

      try {
        console.log("[live-update] downloading bundle", version, url);
        return await CapacitorUpdater.download({ version, url });
      } catch (error) {
        console.error("[live-update] download failed", (error as any)?.message ?? error);
        return null;
      }
    };

    const init = async () => {
      pendingBundle = await resolvePendingBundle();

      if (!pendingBundle) {
        return;
      }

      listenerHandle = await App.addListener("appStateChange", (state: AppState) => {
        console.log("[live-update] app state changed", state);
        if (!state.isActive) {
          console.log("[live-update] applying pending update");
          applyPendingUpdate();
        }
      });
    };

    init();

    return () => {
      console.log("[live-update] Removing listener");
      listenerHandle?.remove();
    };
  }, [config.liveUpdate]);
};

export default useAppLiveUpdates;
